import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of, forkJoin } from "rxjs";
import { delay, tap, finalize } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Filter } from "src/app/user-management/_model/filter.model";
import { GradeMapperService } from "./grade-mapper.service";
import { IGradeV2 } from '../../shared/models/igrade-v2';
import { ProcessingModalService } from '../../_services/processing-modal.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: "root",
})
export class DataService {
  constructor(
    private http: HttpClient, 
    private gradeMapper: GradeMapperService,
    private processingModalService: ProcessingModalService,
    private translate: TranslateService
  ) { }

  currentUser = {
    name: "",
    role_type: "",
    gender: "",
    national_id: "",
    country_code: "",
    phone: "",
    email: "",
    image_path: "",
  };

  private _openFilter = new BehaviorSubject<boolean>(false);
  openFilter = this._openFilter.asObservable();

  toggleFilterStatus(filterStatus: boolean) {
    this._openFilter.next(filterStatus);
  }

  getFilterStatus() {
    return this._openFilter;
  }

  private _changeRowsStyle = new BehaviorSubject<boolean>(false);
  changeRowsStyle = this._changeRowsStyle.asObservable();

  setRowsStyle(compact: boolean) {
    this._changeRowsStyle.next(compact);
  }

  getRowsStyle() {
    return this._changeRowsStyle;
  }

  private _rowsNumberShown = new BehaviorSubject<number>(25);
  rowsNumberShown = this._rowsNumberShown.asObservable();

  setRowsNumberShown(rowsNumber: number) {
    this._rowsNumberShown.next(rowsNumber ?? 25);
  }

  getRowsNumberShown() {
    return this._rowsNumberShown;
  }

  private _searchText = new BehaviorSubject<string>(null);
  searchText = this._searchText.asObservable();

  setSearchText(value: string) {
    this._searchText.next(value);
  }

  getSearchText() {
    return this._searchText;
  }

  private _enrollModalOpened = new BehaviorSubject<boolean>(false);
  enrollModalOpened = this._enrollModalOpened.asObservable();

  toggleEnrollStatus(status: boolean) {
    this._enrollModalOpened.next(status);
  }

  getEnrollStatus() {
    return this._enrollModalOpened;
  }
  // to sent gates from the control bar component to add modals instead of calling the api again
  private _setGates = new BehaviorSubject<any>(null);
  setGates = this._setGates.asObservable();

  sentGates(Gates) {
    this._setGates.next(Gates);
  }

  getGates() {
    return this._setGrades;
  }

  // to sent grades and classes from the control bar component to add modals instead of calling the api again
  private _setGrades = new BehaviorSubject<any>(null);
  setGrades = this._setGrades.asObservable();

  sentGrades(grades) {
    this._setGrades.next(grades);
  }

  getGrades() {
    return this._setGrades;
  }

  private _setClasses = new BehaviorSubject<any>(null);
  setClasses = this._setClasses.asObservable();

  sentClasses(classes) {
    this._setClasses.next(classes);
  }

  getClasses() {
    return this._setClasses;
  }

  getSupervisors() {
    return this.http.get(
      environment.url +
      `admin/schools/${localStorage.getItem(
        "schoolId"
      )}/users?role_type=supervisor&is_paginated=0`
    );
  }

  getTeachers() {
    return this.http.get(
      environment.url +
      `admin/schools/${localStorage.getItem(
        "schoolId"
      )}/users?role_type=teacher&is_paginated=0`
    );
  }

  getAllGrades$() {
    return this.http.get(
      environment.url +
      `admin/schools/${localStorage.getItem("schoolId")}/grades`
    );
  }

  private _setAllGrades = new BehaviorSubject<Array<any>>([]);
  setAllGrades = this._setAllGrades.asObservable();
  allGrades = null;

  setAllGrades$(grades) {
    this._setAllGrades.next(grades);
    this.allGrades = grades;
  }

  getAllGrades() {
    if (this.allGrades == null) {
      this.allGrades = [];

      // this.http.get(environment.url + `admin/schools/${localStorage.getItem('schoolId')}/grades`).subscribe(res=>{
      //     this.setAllGrades$(res);
      // });
    } else {
      return this._setAllGrades;
    }
  }

  getV2Grades$() {
    return this.http.get(
      environment.url +
      `v2/schools/${localStorage.getItem("schoolId")}/grades/all-grades`
    );
  }


  private _setV2Grades = new BehaviorSubject<IGradeV2[]>([]);
  setV2Grades = this._setV2Grades.asObservable();
  v2Grades: IGradeV2[] | null = null;

  setV2Grades$(grades: IGradeV2[]): void {
    this._setV2Grades.next(grades);
    this.v2Grades = grades;
  }
  getV2Grades(): IGradeV2[] {
    return this.v2Grades || [];
  }

  mapGrades(type: string): any[] {
    return this.gradeMapper.map(this.getV2Grades(), type);
  }

  private _setAllGates = new BehaviorSubject<Array<any>>([]);
  setAllGates = this._setAllGates.asObservable();
  allGates = null;

  setAllGates$(gates) {
    this._setAllGates.next(gates);
    this.allGates = gates;
  }
  getAllGates$() {
    return this.http.get(
      environment.url +
      `admin/schools/${localStorage.getItem("schoolId")}/gates`
    );
  }
  getAllGates() {
    if (this.allGates == null) {
      this.allGates = [];
    } else {
      return this._setAllGates;
    }
    // return this.http.get(environment.url + `admin/schools/${localStorage.getItem('schoolId')}/gates`);
  }

  private _setAllRoutes = new BehaviorSubject<Array<any>>([]);
  setAllRoutes = this._setAllRoutes.asObservable();
  allRoutes = null;

  setAllRoutes$(routes) {
    this._setAllRoutes.next(routes);
    this.allRoutes = routes;
  }

  getAllRoutes() {
    if (this.allRoutes == null) {
      this.allRoutes = [];
    } else {
      return this._setAllRoutes;
    }
    // return this.http.get(environment.url + `admin/schools/${localStorage.getItem('schoolId')}/routes`)
  }

  private _setSchoolInfo = new BehaviorSubject<Array<any>>([]);
  setSchoolInfo = this._setSchoolInfo.asObservable();
  schoolInfo = null;

  setSchoolInfo$(school) {
    this._setSchoolInfo.next(school);
    this.schoolInfo = school;
  }

  getSchoolInfo() {
    if (this.schoolInfo == null) {
      this.schoolInfo = [];
    } else {
      return this._setSchoolInfo;
    }
    // return this.http.get(environment.url + `admin/schools/${localStorage.getItem('schoolId')}/routes`)
  }

  getSchoolInfo$() {
    return this.http.get(environment.url + `v2/user/schools`);
  }

  private _pendingRequestsCounter = new BehaviorSubject<number>(0);
  pendingRequestsCounter = this._pendingRequestsCounter.asObservable();
  // private _pendingRequestsFlag = new BehaviorSubject<boolean>(false);
  // pendingRequestsFlag = this._pendingRequestsFlag.asObservable();

  setRequestsCounter(counter) {
    this._pendingRequestsCounter.next(counter);
    // if(counter > 0 ){
    //     this._pendingRequestsFlag.next(true)
    // }
  }

  getRequestsCounter() {
    return this._pendingRequestsCounter;
  }

  private _editRequestsCounter = new BehaviorSubject<number>(0);
  editRequestsCounter = this._editRequestsCounter.asObservable();
  // private _editRequestsFlag = new BehaviorSubject<boolean>(false);
  // editRequestsFlag = this._editRequestsFlag.asObservable();

  setEditRequestsCounter(counter) {
    this._editRequestsCounter.next(counter);
    // if(counter > 0 ){
    //     this._editRequestsFlag.next(true)
    // }
  }

  getEditRequestsCounter() {
    return this._editRequestsCounter;
  }

  private _earlyDismisalCounter = new BehaviorSubject<number>(0);
  earlyDismisalCounter = this._earlyDismisalCounter.asObservable();


  setEarlyDismissalRequestsCounter(counter) {
    this._earlyDismisalCounter.next(counter);
  }

  getEarlyDismissalRequestsCounter() {
    return this._earlyDismisalCounter;
  }

  // Early dismissal displayed count (number of currently loaded items in the list)
  private _earlyDismissalDisplayedCount = new BehaviorSubject<number>(0);
  earlyDismissalDisplayedCount = this._earlyDismissalDisplayedCount.asObservable();

  setEarlyDismissalDisplayedCount(count: number) {
    this._earlyDismissalDisplayedCount.next(count || 0);
  }

  getEarlyDismissalDisplayedCount() {
    return this._earlyDismissalDisplayedCount;
  }

  // getRequestsFlag(){
  //     return this._pendingRequestsFlag;
  // }
  // to sent gates from the control bar component to add modals instead of calling the api again
  private _setRoutes = new BehaviorSubject<any>(null);
  setRoutes = this._setRoutes.asObservable();

  sentRoutes(Routes) {
    this._setRoutes.next(Routes);
  }

  getRoutes() {
    return this._setGrades;
  }

  private _setAllMessages = new BehaviorSubject<Array<any>>([]);
  setAllMessages = this._setAllMessages.asObservable();
  allMessages = null;

  setAllMesssages$(messages) {
    this._setAllMessages.next(messages);
    this.allMessages = messages;
  }

  getAllMessages() {
    if (this.allMessages == null) {
      this.allMessages = [];
      // this.http.get(environment.url + `admin/schools/${localStorage.getItem('schoolId')}/grades`).subscribe(res=>{
      //     this.setAllGrades$(res);
      // });
    } else {
      return this._setAllMessages;
    }
  }

  private _filterValue = new BehaviorSubject<Filter>(new Filter());
  filterValue = this._filterValue.asObservable();

  setFilterValue(value) {
    this._filterValue.next(value);
  }

  getFilterValue() {
    return this._filterValue;
  }

  private _filterFlag = new BehaviorSubject<any>(null);
  filterFlag = this._filterValue.asObservable();

  setFilterFlag(value) {
    this._filterFlag.next(value);
  }

  getFilterFlag() {
    return this._filterFlag;
  }
  private _createRouteData = new BehaviorSubject<any>(null);
  createRouteData = this._createRouteData.asObservable();

  setCreateRouteData(routeData: any) {
    this._createRouteData.next(routeData);
  }

  getCreateRouteData() {
    return this._createRouteData;
  }

  private _closeFilter = new BehaviorSubject<any>(null);
  closeFilter = this._closeFilter.asObservable();

  closeFilterHandler() {
    this._closeFilter.next(false);
  }

  getCloseFilterHandler() {
    return this._closeFilter;
  }

  private _setHealthConditions = new BehaviorSubject<Array<any>>([]);
  setHealthConditions = this._setHealthConditions.asObservable();
  healthConditions = null;

  setHealthConditions$(healthConditions) {
    this._setHealthConditions.next(healthConditions);
    this.healthConditions = healthConditions;
  }
  getHealthConditions$() {
    return this.http.get(environment.url + `v2/settings`);
  }
  getHealthConditions() {
    if (this.healthConditions == null) {
      this.healthConditions = [];
    } else {
      return this._setHealthConditions;
    }
  }

  private _setUsers = new BehaviorSubject<Array<any>>([]);
  setUsers = this._setUsers.asObservable();
  users = null;

  setUsers$(allUsers) {
    this._setUsers.next(allUsers);
    this.users = allUsers;
  }
  getUsers$() {
    return this.http.get(environment.url + `admin/schools/${localStorage.getItem('schoolId')}/users?is_paginated=0`);
  }
  getUsers() {
    if (this.users == null) {
      this.users = [];
    } else {
      return this._setUsers;
    }
  }

  private _isBeta = new BehaviorSubject<any>(false);
  isBeta = this._isBeta.asObservable();

  setIsBeta$(isBeta) {
    this._isBeta.next(isBeta);
  }

  getIsBeta() {
    return this._isBeta;
  }

  // private _isCafeteria = new BehaviorSubject<any>(false);
  // isCafeteria = this._isCafeteria.asObservable();

  // setIsCafeteria$(isCafeteria) {
  //   this._isCafeteria.next(isCafeteria);
  // }

  // getIsCafeteria() {
  //   return this._isCafeteria;
  // }

  getAllSections() {
    return this.http.get(environment.url + `v2/admin/schools/${localStorage.getItem('schoolId')}/sections/list`);
  }

  private _isBusBeta = new BehaviorSubject<any>(false);
  isBusBeta = this._isBusBeta.asObservable();

  setIsBusBeta$(isBusBeta) {
    this._isBusBeta.next(isBusBeta);
  }

  getIsBusBeta() {
    return this._isBusBeta;
  }

  private _isCallingScreenBeta = new BehaviorSubject<any>(false);
  isCallingScreenBeta = this._isCallingScreenBeta.asObservable();

  setIsCallingScreenBeta$(isCallingScreenBeta) {
    this._isCallingScreenBeta.next(isCallingScreenBeta);
  }

  getIsCallingScreenBeta() {
    return this._isCallingScreenBeta;
  }

  private _isBulkUploadStudentsBeta = new BehaviorSubject<any>(false);
  isBulkUploadStudentsBeta = this._isBulkUploadStudentsBeta.asObservable();

  setIsBulkUploadStudentsBeta$(isBulkUploadStudentsBeta) {
    this._isBulkUploadStudentsBeta.next(isBulkUploadStudentsBeta);
  }

  getIsBulkUploadStudentsBeta() {
    return this._isBulkUploadStudentsBeta;
  }

  private _isCallingScreenGamification = new BehaviorSubject<any>(false);
  isCallingScreenGamification = this._isCallingScreenGamification.asObservable();

  setIsCallingScreenGamification$(isCallingScreenGamification) {
    this._isCallingScreenGamification.next(isCallingScreenGamification);
  }

  getIsCallingScreenGamification() {
    return this._isCallingScreenGamification;
  }

  private _isGeminiVoiceWidget = new BehaviorSubject<any>(false);
  isGeminiVoiceWidget = this._isGeminiVoiceWidget.asObservable();

  setIsGeminiVoiceWidget$(isGeminiVoiceWidget) {
    this._isGeminiVoiceWidget.next(isGeminiVoiceWidget);
  }

  getIsGeminiVoiceWidget() {
    return this._isGeminiVoiceWidget;
  }

  private _isPioneerIntegrationFlow = new BehaviorSubject<any>(false);
  isPioneerIntegrationFlow = this._isPioneerIntegrationFlow.asObservable();

  setIsPioneerIntegrationFlow$(isPioneerIntegrationFlow) {
    this._isPioneerIntegrationFlow.next(isPioneerIntegrationFlow);
  }

  getIsPioneerIntegrationFlow() {
    return this._isPioneerIntegrationFlow;
  }

  // Centralized cache update methods - Observable versions (for chaining business logic)
  updateGradesCache$(delayMs: number = 0): Observable<any> {
    if (delayMs > 0) {
      // Show processing modal when there's a delay
      this.processingModalService.showProcessing('processing-modal.updating-grades');
      return of(null).pipe(
        delay(delayMs),
        tap(() => {
          // After delay, start the actual API call
          this.getAllGrades$().subscribe({
            next: (res) => {
              this.allGrades = res;
              this.setAllGrades$(this.allGrades);
            },
            complete: () => {
              // Show completion and then hide processing modal
              this.processingModalService.showCompletion();
            }
          });
        })
      );
    } else {
      // No delay, just return the observable with completion handling
      return this.getAllGrades$().pipe(
        tap(res => {
          this.allGrades = res;
          this.setAllGrades$(this.allGrades);
        }),
        finalize(() => {
          // Show completion and then hide processing modal
          this.processingModalService.showCompletion();
        })
      );
    }
  }

  updateGatesCache$(delayMs: number = 0): Observable<any> {
    if (delayMs > 0) {
      // Show processing modal when there's a delay
      this.processingModalService.showProcessing('processing-modal.updating-gates');
      return of(null).pipe(
        delay(delayMs),
        tap(() => {
          // After delay, start the actual API call
          this.getAllGates$().subscribe({
            next: (res) => {
              this.allGates = res;
              this.setAllGates$(this.allGates);
            },
            complete: () => {
              // Show completion and then hide processing modal
              this.processingModalService.showCompletion();
            }
          });
        })
      );
    } else {
      // No delay, just return the observable with completion handling
      return this.getAllGates$().pipe(
        tap(res => {
          this.allGates = res;
          this.setAllGates$(this.allGates);
        }),
        finalize(() => {
          // Show completion and then hide processing modal
          this.processingModalService.showCompletion();
        })
      );
    }
  }

  updateAllCache$(delayMs: number = 0): Observable<any> {
    if (delayMs > 0) {
      // Show processing modal when there's a delay
      this.processingModalService.showProcessing('processing-modal.updating-all');
      return of(null).pipe(
        delay(delayMs),
        tap(() => {
          // After delay, start the actual API calls
          forkJoin([
            this.getAllGrades$(),
            this.getAllGates$()
          ]).subscribe({
            next: ([grades, gates]) => {
              this.allGrades = grades;
              this.allGates = gates;
              this.setAllGrades$(this.allGrades);
              this.setAllGates$(this.allGates);
            },
            complete: () => {
              // Show completion and then hide processing modal
              this.processingModalService.showCompletion();
            }
          });
        })
      );
    } else {
      // No delay, just return the observable with completion handling
      return forkJoin([
        this.getAllGrades$(),
        this.getAllGates$()
      ]).pipe(
        tap(([grades, gates]) => {
          this.allGrades = grades;
          this.allGates = gates;
          this.setAllGrades$(this.allGrades);
          this.setAllGates$(this.allGates);
        }),
        finalize(() => {
          // Show completion and then hide processing modal
          this.processingModalService.showCompletion();
        })
      );
    }
  }

  // -----------------------------------------------------------------------------------------

  // Centralized cache update methods - Fire-and-forget versions (no subscription needed)
  updateGradesCache(delayMs: number = 0): void {
    if (delayMs > 0) {
      this.processingModalService.showProcessing('processing-modal.updating-grades');
      setTimeout(() => {
        this.getAllGrades$().subscribe({
          next: (res) => {
            this.allGrades = res;
            this.setAllGrades$(this.allGrades);
          },
          complete: () => {
            this.processingModalService.showCompletion();
          }
        });
      }, delayMs);
    } else {
      this.getAllGrades$().subscribe({
        next: (res) => {
          this.allGrades = res;
          this.setAllGrades$(this.allGrades);
        },
        complete: () => {
          this.processingModalService.showCompletion();
        }
      });
    }
  }

  updateGatesCache(delayMs: number = 0): void {
    if (delayMs > 0) {
      this.processingModalService.showProcessing('processing-modal.updating-gates');
      setTimeout(() => {
        this.getAllGates$().subscribe({
          next: (res) => {
            this.allGates = res;
            this.setAllGates$(this.allGates);
          },
          complete: () => {
            this.processingModalService.showCompletion();
          }
        });
      }, delayMs);
    } else {
      this.getAllGates$().subscribe({
        next: (res) => {
          this.allGates = res;
          this.setAllGates$(this.allGates);
        },
        complete: () => {
          this.processingModalService.showCompletion();
        }
      });
    }
  }

  updateAllCache(delayMs: number = 0): void {
    if (delayMs > 0) {
      this.processingModalService.showProcessing('processing-modal.updating-all');
      setTimeout(() => {
        this.updateGradesCache();
        this.updateGatesCache();
        // Note: Individual cache methods will handle showing completion
      }, delayMs);
    } else {
      this.updateGradesCache();
      this.updateGatesCache();
    }
  }

  // -----------------------------------------------------------------------------------------
  // Get specific grades by IDs using the API
  getGradesById$(gradeIds: number[]): Observable<any> {
    const schoolId = localStorage.getItem("schoolId");
    const params = gradeIds.map((id, index) => `grade_ids[${index}]=${id}`).join('&');
    return this.http.get(
      environment.url + `admin/schools/${schoolId}/grades?${params}`
    );
  }

  // Update grades cache by specific IDs
  updateGradeCashById(gradeIds: number[], delayMs: number = 0): void {
    if (delayMs > 0) {
      this.processingModalService.showProcessing('processing-modal.updating-grades');
      setTimeout(() => {
        this.getGradesById$(gradeIds).subscribe({
          next: (res) => {
            // Find and update specific grades in the cache
            if (this.allGrades && Array.isArray(res)) {
              res.forEach(updatedGrade => {
                const index = this.allGrades.findIndex(grade => grade.id === updatedGrade.id);
                if (index !== -1) {
                  this.allGrades[index] = updatedGrade;
                } else {
                  // If grade doesn't exist in cache, add it
                  this.allGrades.push(updatedGrade);
                }
              });
              this.setAllGrades$(this.allGrades);
            } else {
              // If no cache exists, set the response as the new cache
              this.allGrades = res;
              this.setAllGrades$(this.allGrades);
            }
          },
          complete: () => {
            this.processingModalService.showCompletion();
          }
        });
      }, delayMs);
    } else {
      this.getGradesById$(gradeIds).subscribe({
        next: (res) => {
          // Find and update specific grades in the cache
          if (this.allGrades && Array.isArray(res)) {
            res.forEach(updatedGrade => {
              const index = this.allGrades.findIndex(grade => grade.id === updatedGrade.id);
              if (index !== -1) {
                this.allGrades[index] = updatedGrade;
              } else {
                // If grade doesn't exist in cache, add it
                this.allGrades.push(updatedGrade);
              }
            });
            this.setAllGrades$(this.allGrades);
          } else {
            // If no cache exists, set the response as the new cache
            this.allGrades = res;
            this.setAllGrades$(this.allGrades);
          }
        },
        complete: () => {
          this.processingModalService.showCompletion();
        }
      });
    }
  }

  // Update specific grades by IDs and all gates
  updateGradesByIdAndAllGates(gradeIds: number[], delayMs: number = 0): void {
    if (delayMs > 0) {
      this.processingModalService.showProcessing('processing-modal.updating-all');
      setTimeout(() => {
        this.updateGradeCashById(gradeIds);
        this.updateGatesCache();
        // Note: Individual cache methods will handle showing completion
      }, delayMs);
    } else {
      this.updateGradeCashById(gradeIds);
      this.updateGatesCache();
    }
  }
  // Early dismissal filters (type/status)
  private _earlyDismissalTypeFilter = new BehaviorSubject<string | null>(null);
  private _earlyDismissalStatusFilter = new BehaviorSubject<string | null>(null);

  setEarlyDismissalTypeFilter(value: string | null) {
    this._earlyDismissalTypeFilter.next(value);
  }
  getEarlyDismissalTypeFilter() {
    return this._earlyDismissalTypeFilter;
  }

  setEarlyDismissalStatusFilter(value: string | null) {
    this._earlyDismissalStatusFilter.next(value);
  }
  getEarlyDismissalStatusFilter() {
    return this._earlyDismissalStatusFilter;
  }
}
